import React, { useContext, useEffect, useState } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import { SidebarView, SidebarContextProvider } from "../../components/Sidebar";
import IntegrationBeforeYouBegin from "./IntegrationBeforeYouBegin";
import IntegrationAuthPage from "./IntegrationAuthPage";
import IntegrationConnectionPage from "./IntegrationConnectionPage";
import IntegrationEvents from "./IntegrationEvents";

const DEFAULT_CONFIG = { values: {} };

function normalizeConfig(raw) {
    if (!raw || typeof raw !== "object") return DEFAULT_CONFIG;
    return { values: raw.values ?? {} };
}

const GET_INTEGRATION = gql`
    query GetIntegration($where: IntegrationWhereUniqueInput!) {
        integration(where: $where) {
            id
            name
            description
            integration_key
            config
            is_active
            plant_id
            subscriptions {
                id
                entity_type_id
                action_mask
            }
        }
    }
`;

const CREATE_ONE_INTEGRATION = gql`
    mutation CreateOneIntegration($data: IntegrationCreateInput!) {
        createOneIntegration(data: $data) {
            id
            name
            description
            integration_key
            config
            is_active
            subscriptions {
                id
                entity_type_id
                action_mask
            }
        }
    }
`;

const UPDATE_ONE_INTEGRATION = gql`
    mutation UpdateOneIntegration(
        $where: IntegrationWhereUniqueInput!
        $data: IntegrationUpdateInput!
    ) {
        updateOneIntegration(where: $where, data: $data) {
            id
            name
            description
            integration_key
            config
            is_active
            subscriptions {
                id
                entity_type_id
                action_mask
            }
        }
    }
`;

export const DELETE_ONE_INTEGRATION = gql`
    mutation DeleteOneIntegration($where: IntegrationWhereUniqueInput!) {
        deleteOneIntegration(where: $where) {
            id
        }
    }
`;

// `template` is the resolved integrations.available catalog entry — required
// on create (locks integration_key + drives the config_schema form), looked
// up by the parent from `item.integration_key` on edit.
function IntegrationSidebar({ show, item, template, onDiscard, onSave, onDelete }) {
    const starekContext = useContext(StarekContext);
    const [preparedEntity, setPreparedEntity] = useState(null);

    const [createOneIntegration] = useMutation(CREATE_ONE_INTEGRATION, {
        client: starekContext.engineClient,
    });

    const [updateOneIntegration] = useMutation(UPDATE_ONE_INTEGRATION, {
        client: starekContext.engineClient,
    });

    const { data: integrationData, loading: loadingIntegration } = useQuery(
        GET_INTEGRATION,
        {
            client: starekContext.engineClient,
            variables: { where: { id: item?.id } },
            skip: !item?.id || !show,
            fetchPolicy: "network-only",
        },
    );

    // Prepare entity when data arrives or when opening for a new integration
    useEffect(() => {
        if (!show) {
            setPreparedEntity(null);
            return;
        }

        if (!item?.id) {
            const plantName = starekContext.lastUsedPlant?.name;
            setPreparedEntity({
                name: template?.name
                    ? `${plantName ? `${plantName} <> ` : ""}${template.name}`
                    : "",
                description: template?.description ?? "",
                integration_key: template?.key,
                config: DEFAULT_CONFIG,
                is_active: true,
                plant_id: starekContext.lastUsedPlant?.id,
                subscriptions: [],
            });
            return;
        }

        if (integrationData?.integration) {
            const i = integrationData.integration;
            setPreparedEntity({
                ...i,
                config: normalizeConfig(i.config),
                subscriptions: i.subscriptions ?? [],
            });
        }
    }, [show, item?.id, integrationData, template]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSaveInternal = async (emittedData) => {
        try {
            const { name, subscriptions = [] } = emittedData;

            if (!name?.trim()) {
                starekContext.showError("Integration name is required");
                return;
            }

            const config = normalizeConfig(emittedData.config);
            const plantId = emittedData.plant_id || starekContext.lastUsedPlant?.id;
            const integrationKey = emittedData.integration_key || template?.key;

            const subscriptionInput = subscriptions.map((s) => ({
                entity_type: { connect: { id: Number(s.entity_type_id) } },
                action_mask: s.action_mask ?? 7,
            }));

            if (!item?.id) {
                await createOneIntegration({
                    variables: {
                        data: {
                            name: name.trim(),
                            description: emittedData.description || null,
                            integration_key: integrationKey,
                            config,
                            is_active: emittedData.is_active ?? true,
                            plant: { connect: { id: Number(plantId) } },
                            created_by: { connect: { id: 1 } },
                            updated_by: { connect: { id: 1 } },
                            ...(subscriptionInput.length > 0
                                ? { subscriptions: { create: subscriptionInput } }
                                : {}),
                        },
                    },
                });
            } else {
                await updateOneIntegration({
                    variables: {
                        where: { id: Number(item.id) },
                        data: {
                            name: { set: name.trim() },
                            description: { set: emittedData.description || null },
                            config: { set: config },
                            is_active: { set: emittedData.is_active ?? true },
                            updated_by: { connect: { id: 1 } },
                            subscriptions: {
                                deleteMany: {},
                                ...(subscriptionInput.length > 0
                                    ? { create: subscriptionInput }
                                    : {}),
                            },
                        },
                    },
                });
            }

            starekContext.showSuccess(
                item?.id ? "Integration updated successfully" : "Integration created successfully",
            );

            if (onSave) await onSave();
        } catch (error) {
            console.error("Error saving integration:", error);
            starekContext.showError(error?.message || "Failed to save integration");
            throw error;
        }
    };

    const handleDeleteInternal = async () => {
        try {
            if (onDelete) await onDelete(item);
        } catch (error) {
            console.error("Error deleting integration:", error);
            starekContext.showError(error?.message || "Failed to delete integration");
            throw error;
        }
    };

    if (loadingIntegration && item?.id) return null;

    return (
        <SidebarContextProvider
            entity={preparedEntity}
            show={show}
            onSave={handleSaveInternal}
            onDelete={item?.id ? handleDeleteInternal : null}
            onDiscard={onDiscard}
        >
            <SidebarView entityName={template?.name ? `${template.name} integration` : "Integration"} entityCode="integration">
                <SidebarView.Page name="before_you_begin" title="Basic Details">
                    <IntegrationBeforeYouBegin template={template} />
                </SidebarView.Page>
                <SidebarView.Page name="authentication" title="Authentication">
                    <IntegrationAuthPage template={template} />
                </SidebarView.Page>
                <SidebarView.Page name="connection" title="Connection">
                    <IntegrationConnectionPage template={template} />
                </SidebarView.Page>
                <SidebarView.Page name="events" title="Subscriptions">
                    <IntegrationEvents />
                </SidebarView.Page>
            </SidebarView>
        </SidebarContextProvider>
    );
}

export default IntegrationSidebar;
